import posthog from 'posthog-js';

/**
 * Referral / affiliate link capture.
 *
 * A shared link looks like:
 *   https://www.timohelps.com/?ref=ABCD2345        (friend referral)
 *   https://www.timohelps.com/?affiliate=CRAB23CD  (creator / affiliate programme)
 *
 * Both links are minted server-side (server/referral-helper.js getOrCreateCode,
 * server/affiliate-helper.js getOrCreateAffiliate). The code has to survive the
 * hop from that link to signup, where the readers pick it back up and POST it
 * to /api/referral/attribute and /api/affiliate/attribute.
 *
 * The storage medium and the key names here are NOT a free choice: they are
 * dictated by the readers that already exist. Changing either silently breaks
 * referral credits and affiliate payouts.
 *
 *   src/pages/Login.jsx:93,116          sessionStorage ttt_ref_code / ttt_aff_code
 *   src/pages/AuthCallback.jsx:149,172  sessionStorage ttt_ref_code / ttt_aff_code
 *   src/pages/Pricing.jsx:209           sessionStorage ttt_ref_code (tags Stripe checkout)
 *
 * Attribution model: LAST TOUCH. The most recent valid code in this tab wins,
 * because the link the visitor actually clicked last is the one that brought
 * them. The server is the backstop that stops later re-attribution: once a
 * referee email has a row, attributeReferral / attributeAffiliate return
 * `idempotent` and never move an existing customer to a new referrer.
 *
 * Age: the readers do not expect a timestamp, and sessionStorage already dies
 * with the tab, so an expiry is mostly belt and braces. We still stamp the
 * capture and drop anything older than MAX_AGE_MS on a later call, so a code
 * sitting in a tab that has been open for weeks cannot be attributed to a
 * signup that has nothing to do with it.
 */

export const REF_CODE_KEY = 'ttt_ref_code';
export const AFF_CODE_KEY = 'ttt_aff_code';

// Written and read only by this module. The signup readers delete the code key
// and leave this behind, which is fine: an orphan stamp is cleaned up below.
const REF_STAMP_KEY = 'ttt_ref_code_at';
const AFF_STAMP_KEY = 'ttt_aff_code_at';

// 30 days. Long enough that a slow decider still credits their friend, short
// enough that a forgotten tab does not.
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

// Longest raw value we will even look at, before validation.
const MAX_RAW_LENGTH = 64;

/**
 * Minted codes are 8 characters from ABCDEFGHJKLMNPQRSTUVWXYZ23456789
 * (referral, CODE_LENGTH 8) or the same alphabet behind a CR prefix
 * (affiliate, CODE_PREFIX + CODE_RANDOM_LEN 6). We deliberately accept a
 * slightly wider shape than that: uppercase alphanumerics and hyphens, 4 to 24
 * characters. Hand-inserted vanity codes in Supabase would fail an exact-
 * alphabet check and cost someone real money, while this shape still rejects
 * anything that could be an injected payload. The authoritative check is the
 * server lookup against referral_codes / affiliates, which rejects a code that
 * does not exist.
 *
 * Underscore is intentionally NOT allowed: Pricing.jsx joins the Stripe
 * client_reference_id parts with '__' and the webhook splits on it
 * (server/index.js:706), so an underscore in a code would corrupt that parse.
 */
const CODE_SHAPE = /^[A-Z0-9-]{4,24}$/;

/**
 * Normalise a raw query value into a storable code, or null if it is not one.
 * @param {unknown} raw
 * @returns {string|null}
 */
export function normalizeCode(raw) {
  if (typeof raw !== 'string') return null;
  if (raw.length > MAX_RAW_LENGTH) return null;
  const code = raw.trim().toUpperCase();
  if (!CODE_SHAPE.test(code)) return null;
  return code;
}

function readItem(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeItem(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function removeItem(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* private mode: nothing to clean up */
  }
}

/**
 * Drop a stored code that has aged out, and drop a stamp whose code is already
 * gone (the signup readers remove the code and leave the stamp behind).
 * A stored code with no stamp is treated as fresh and stamped now, so codes
 * captured before this module existed are never thrown away.
 */
function pruneStale(codeKey, stampKey, now) {
  const code = readItem(codeKey);
  const stamp = readItem(stampKey);
  if (!code) {
    if (stamp) removeItem(stampKey);
    return;
  }
  const at = Number(stamp);
  if (!stamp || !Number.isFinite(at) || at > now) {
    writeItem(stampKey, String(now));
    return;
  }
  if (now - at > MAX_AGE_MS) {
    removeItem(codeKey);
    removeItem(stampKey);
  }
}

/**
 * Store one code if the URL carries a valid one.
 * @returns {{code: string|null, stored: boolean, replaced: boolean}}
 */
function captureOne(params, param, codeKey, stampKey, now) {
  const incoming = normalizeCode(params.get(param));
  const existing = readItem(codeKey);

  // No usable code in the URL: never let a missing or malformed value wipe a
  // code we already hold.
  if (!incoming) return { code: existing || null, stored: false, replaced: false };

  // Idempotent: the same code arriving again (re-render, back button, a second
  // call from another route) is not a new capture and fires no event.
  if (existing === incoming) return { code: existing, stored: false, replaced: false };

  if (!writeItem(codeKey, incoming)) {
    // Storage blocked (private mode / iOS lockdown). Nothing to report, and the
    // page must not break because of it.
    return { code: existing || null, stored: false, replaced: false };
  }
  writeItem(stampKey, String(now));
  return { code: incoming, stored: true, replaced: Boolean(existing) };
}

/**
 * Capture ?ref= and ?affiliate= from the current URL into sessionStorage.
 *
 * Safe to call from anywhere and as often as you like: it never throws, works
 * when storage is blocked, and only reports a capture when the stored value
 * actually changes.
 *
 * @param {string} [search] query string to read; defaults to window.location.search
 * @returns {{ref: string|null, affiliate: string|null}} the codes now held
 */
export function captureReferralParams(search) {
  if (typeof window === 'undefined') return { ref: null, affiliate: null };
  try {
    const now = Date.now();
    pruneStale(REF_CODE_KEY, REF_STAMP_KEY, now);
    pruneStale(AFF_CODE_KEY, AFF_STAMP_KEY, now);

    const params = new URLSearchParams(
      typeof search === 'string' ? search : window.location.search || ''
    );

    const ref = captureOne(params, 'ref', REF_CODE_KEY, REF_STAMP_KEY, now);
    const affiliate = captureOne(params, 'affiliate', AFF_CODE_KEY, AFF_STAMP_KEY, now);

    // Landing on a referral link is the first thing in the chain that the
    // founder can see. Without this, referral traffic is invisible until
    // someone signs up. Names are new: the existing referral events are
    // *_attributed / *_attribute_failed / *_attribute_error (Login.jsx,
    // AuthCallback.jsx) and the share-side referral_link_copied (Refer.jsx).
    try {
      if (ref.stored) {
        posthog?.capture('referral_code_captured', {
          code: ref.code,
          replaced: ref.replaced,
          path: window.location.pathname,
        });
      }
      if (affiliate.stored) {
        posthog?.capture('affiliate_code_captured', {
          code: affiliate.code,
          replaced: affiliate.replaced,
          path: window.location.pathname,
        });
      }
    } catch {
      /* analytics must never break a landing */
    }

    return { ref: ref.code, affiliate: affiliate.code };
  } catch {
    return { ref: null, affiliate: null };
  }
}

/**
 * Current stored codes, without touching the URL. Handy for debugging and for
 * any caller that wants to branch on "did this visitor arrive via a link".
 * @returns {{ref: string|null, affiliate: string|null}}
 */
export function readCapturedCodes() {
  if (typeof window === 'undefined') return { ref: null, affiliate: null };
  return {
    ref: readItem(REF_CODE_KEY),
    affiliate: readItem(AFF_CODE_KEY),
  };
}

export default captureReferralParams;
